import React from 'react';
import { useLocation, Link as RouterLink } from 'react-router-dom';

import { Link as GgLink } from '@gogame/components';

import { useScreenMessage } from './Message';
import { useLinkBasename } from './ScreensContext';

export const useScreenLink = (
  { link, searchParams, pathParams, params = {} },
  active = true
) => {
  const { search, pathname, hash } = useLocation();
  const keepLocation = searchParams || pathParams;

  const basename = useLinkBasename(link);

  if (!link || !active) {
    return undefined;
  }

  if (keepLocation) {
    const to = { search, pathname, hash };
    if (pathParams) {
      to.pathname = link.pathParams.stringify(pathParams, basename);
    }

    if (searchParams) {
      to.search = link.searchParams
        ? link.searchParams.stringify(searchParams)
        : '';
    }

    return to;
  }

  return link.location(params, { basename: basename });
};

const ScreenLink = React.forwardRef(
  (
    {
      link,
      searchParams,
      pathParams,
      params,
      onMouseEnter,
      fast = false,
      ...props
    },
    _ref
  ) => {
    const { showSuccessMessage } = useScreenMessage();
    const [active, setActive] = React.useState(!fast);
    const linkRef = React.useRef(null);
    const to = useScreenLink(
      {
        link,
        searchParams,
        pathParams,
        params,
      },
      active
    );

    const handleCopy = (href) => {
      showSuccessMessage(`${href} copied`);
    };

    const handleMouseEnter = React.useCallback(
      (e) => {
        if (onMouseEnter) {
          onMouseEnter(e);
        }
        if (!active) {
          setActive(true);
        }
      },
      [active, onMouseEnter]
    );

    if (fast && props.button) {
      // @plarium/ui/Button doesn't work with ref
      throw new Error(`fast link doesn't work with button`);
    }

    return (
      <GgLink
        component={RouterLink}
        ref={linkRef}
        {...props}
        to={to || ''}
        onMouseEnter={handleMouseEnter}
        onCopy={handleCopy}
      />
    );
  }
);

export default ScreenLink;
